import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';

/**
 * Visual Content Editor Component
 * Allows admins to edit website content, images, bios, and other dynamic elements
 * without requiring code knowledge
 */

const VisualContentEditor = () => {
  const { currentColors } = useAdminTheme();
  const [activeSection, setActiveSection] = useState('hero');
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [contentData, setContentData] = useState({
    hero: {
      title: 'Global Capability Center Solutions',
      subtitle: 'Strategic Outsourcing for Enterprise Growth',
      image: '/images/hero-bg.jpg',
      cta_text: 'Get Started',
      cta_link: '/contact'
    },
    about: {
      title: 'About BootMinds Elite',
      description: 'Leading Global Capability Center consulting firm...',
      image: '/images/about.jpg',
      team_size: '50+',
      years_experience: '15+'
    },
    team: [
      {
        id: 1,
        name: 'Sanjay Jain',
        role: 'Founder & CEO',
        bio: 'Strategic leader with 15+ years in GCC consulting',
        image: '/images/team/sanjay.jpg',
        social: { linkedin: '', twitter: '' }
      },
      {
        id: 2,
        name: 'Swetha Jain',
        role: 'Co-Founder & COO',
        bio: 'Operations expert specializing in process optimization',
        image: '/images/team/swetha.jpg',
        social: { linkedin: '', twitter: '' }
      }
    ],
    services: [
      {
        id: 1,
        title: 'Software Development',
        description: 'Custom software solutions and development services',
        icon: 'Code',
        image: '/images/services/dev.jpg'
      },
      {
        id: 2,
        title: 'Data Analytics',
        description: 'Advanced analytics and business intelligence',
        icon: 'BarChart3',
        image: '/images/services/analytics.jpg'
      }
    ],
    testimonials: [
      {
        id: 1,
        name: 'John Smith',
        company: 'TechCorp Solutions',
        text: 'BootMinds Elite transformed our operations...',
        rating: 5,
        image: '/images/testimonials/john.jpg'
      }
    ]
  });

  const [formData, setFormData] = useState({});

  // Sections available for editing
  const sections = [
    { id: 'hero', label: 'Hero Section', icon: 'Image' },
    { id: 'about', label: 'About Section', icon: 'Info' },
    { id: 'team', label: 'Team Members', icon: 'Users' },
    { id: 'services', label: 'Services', icon: 'Briefcase' },
    { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' }
  ];

  // Handle edit click
  const handleEdit = (item, section) => {
    setEditingItem({ ...item, section });
    setFormData({ ...item });
    setShowModal(true);
  };

  // Handle add new item
  const handleAddNew = (section) => {
    const templates = {
      team: { name: '', role: '', bio: '', image: '', social: { linkedin: '', twitter: '' } },
      services: { title: '', description: '', icon: 'Briefcase', image: '' },
      testimonials: { name: '', company: '', text: '', rating: 5, image: '' }
    };
    
    setEditingItem({ section, isNew: true });
    setFormData(templates[section] || {});
    setShowModal(true);
  };

  // Handle save
  const handleSave = () => {
    if (editingItem.section === 'hero' || editingItem.section === 'about') {
      setContentData(prev => ({
        ...prev,
        [editingItem.section]: formData
      }));
    } else {
      // For array sections
      if (editingItem.isNew) {
        setContentData(prev => ({
          ...prev,
          [editingItem.section]: [...prev[editingItem.section], { ...formData, id: Date.now() }]
        }));
      } else {
        setContentData(prev => ({
          ...prev,
          [editingItem.section]: prev[editingItem.section].map(item =>
            item.id === editingItem.id ? { ...item, ...formData } : item
          )
        }));
      }
    }
    setShowModal(false);
    setEditingItem(null);
  };

  // Handle delete
  const handleDelete = (id, section) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setContentData(prev => ({
        ...prev,
        [section]: prev[section].filter(item => item.id !== id)
      }));
    }
  };

  // Render content preview
  const renderPreview = () => {
    switch (activeSection) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div
              className="p-6 rounded-lg border"
              style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                    {contentData.hero.title}
                  </h3>
                  <p style={{ color: currentColors.textMuted }}>{contentData.hero.subtitle}</p>
                </div>
                <button
                  onClick={() => handleEdit(contentData.hero, 'hero')}
                  className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
                >
                  <Icon name="Edit" size={18} style={{ color: currentColors.primary }} />
                </button>
              </div>
              {contentData.hero.image && (
                <img
                  src={contentData.hero.image}
                  alt="Hero"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}
              <div className="flex gap-2">
                <span
                  className="px-3 py-1 rounded text-sm"
                  style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                >
                  {contentData.hero.cta_text}
                </span>
              </div>
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="space-y-4">
            <button
              onClick={() => handleAddNew('team')}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              style={{ backgroundColor: currentColors.primary, color: '#fff' }}
            >
              <Icon name="Plus" size={16} />
              Add Team Member
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contentData.team.map((member) => (
                <div
                  key={member.id}
                  className="p-4 rounded-lg border"
                  style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
                >
                  {member.image && (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-40 object-cover rounded-lg mb-3"
                    />
                  )}
                  <h4 className="font-bold" style={{ color: currentColors.text }}>
                    {member.name}
                  </h4>
                  <p className="text-sm" style={{ color: currentColors.textMuted }}>
                    {member.role}
                  </p>
                  <p className="text-sm mt-2" style={{ color: currentColors.textSecondary }}>
                    {member.bio}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleEdit(member, 'team')}
                      className="flex-1 px-3 py-1 rounded text-sm flex items-center justify-center gap-1"
                      style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                    >
                      <Icon name="Edit" size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(member.id, 'team')}
                      className="flex-1 px-3 py-1 rounded text-sm flex items-center justify-center gap-1"
                      style={{ backgroundColor: currentColors.error, color: '#fff' }}
                    >
                      <Icon name="Trash2" size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="space-y-4">
            <button
              onClick={() => handleAddNew('services')}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              style={{ backgroundColor: currentColors.primary, color: '#fff' }}
            >
              <Icon name="Plus" size={16} />
              Add Service
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contentData.services.map((service) => (
                <div
                  key={service.id}
                  className="p-4 rounded-lg border"
                  style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
                >
                  {service.image && (
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  )}
                  <h4 className="font-bold" style={{ color: currentColors.text }}>
                    {service.title}
                  </h4>
                  <p className="text-sm mt-2" style={{ color: currentColors.textSecondary }}>
                    {service.description}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleEdit(service, 'services')}
                      className="flex-1 px-3 py-1 rounded text-sm flex items-center justify-center gap-1"
                      style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                    >
                      <Icon name="Edit" size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(service.id, 'services')}
                      className="flex-1 px-3 py-1 rounded text-sm flex items-center justify-center gap-1"
                      style={{ backgroundColor: currentColors.error, color: '#fff' }}
                    >
                      <Icon name="Trash2" size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div style={{ color: currentColors.textMuted }}>Select a section to edit</div>;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
          Visual Content Editor
        </h2>
        <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
          Edit website content, images, and text without coding
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div
          className="rounded-lg border p-4 h-fit"
          style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
        >
          <h3 className="font-bold mb-4" style={{ color: currentColors.text }}>
            Sections
          </h3>
          <div className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeSection === section.id ? 'shadow-sm' : 'hover:bg-opacity-10 hover:bg-muted-foreground'
                }`}
                style={{
                  backgroundColor: activeSection === section.id ? currentColors.primary : 'transparent',
                  color: activeSection === section.id ? '#fff' : currentColors.text
                }}
              >
                <Icon name={section.icon} size={18} />
                <span className="text-sm font-medium">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          {renderPreview()}
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="bg-card rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                {editingItem?.isNew ? 'Add New' : 'Edit'} {editingItem?.section}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Dynamic form fields based on section */}
              {editingItem?.section === 'hero' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Subtitle
                    </label>
                    <input
                      type="text"
                      value={formData.subtitle || ''}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Image URL
                    </label>
                    <input
                      type="text"
                      value={formData.image || ''}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      CTA Button Text
                    </label>
                    <input
                      type="text"
                      value={formData.cta_text || ''}
                      onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      CTA Button Link
                    </label>
                    <input
                      type="text"
                      value={formData.cta_link || ''}
                      onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                </>
              )}

              {editingItem?.section === 'team' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Role/Position
                    </label>
                    <input
                      type="text"
                      value={formData.role || ''}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Bio
                    </label>
                    <textarea
                      value={formData.bio || ''}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Profile Image URL
                    </label>
                    <input
                      type="text"
                      value={formData.image || ''}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      LinkedIn URL
                    </label>
                    <input
                      type="text"
                      value={formData.social?.linkedin || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social: { ...formData.social, linkedin: e.target.value }
                      })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                </>
              )}

              {editingItem?.section === 'services' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Service Title
                    </label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Description
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                      Service Image URL
                    </label>
                    <input
                      type="text"
                      value={formData.image || ''}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: currentColors.background,
                        borderColor: currentColors.border,
                        color: currentColors.text
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{ backgroundColor: currentColors.border, color: currentColors.text }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualContentEditor;
